import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { RSInput, RSTextarea, RSButton, RSCheckbox } from 'reactsymbols-kit';
import { Row, Col } from '../Grid';
import { orderBy } from 'lodash';
import EditObjectController from '../../merode/controllers/EditObjectController';
import AttributesInspector from './AttributesInspector';
import MethodPopover from '../MethodPopover';
import InspectorTabs from './InspectorTabs';
import MultiplePropagationConstraintsInspector from './MultiplePropagationConstraintsInspector';
import Styles from '../../styles/colors';
import Checkbox from '../Checkbox';
import type { IObjectType } from '../../stores/model/metamodel/object_type';
import type { IObjectInspector } from '../../stores/ui/object_inspector';
import type { IModelVersion } from '../../merode/controllers/Controller';
import type Report from '../../merode/checks/Report';
import type { AttributeUpdates, MPCUpdates } from '../../merode/commands/types';
import {
  asRSChange,
  rsValue,
  type RSInputChange,
  type RSTextareaChange,
  type RSCheckboxChange,
} from './rsForm';

interface ObjectInspectorProps {
  /** The ModelVersion (to pass to the controller). */
  version: IModelVersion;
  /** The object to inspect. */
  object: IObjectType;
  /** The ObjectInspector ui store. */
  ui: IObjectInspector;
  /** Callback to call when a report is generated. */
  onReport?: (report: Report) => void;
  /** Callback to call when the object should be deleted. */
  onDelete?: (id: string) => void;
  /** Callback to close the inspector. */
  requestClose?: () => void;
}

/**
 * Object inspector.
 *
 * Props:
 *
 * - version: The ModelVersion (to pass to the controller)
 * - object: the object to inspect
 * - ui: the ObjectInspector store
 * - onReport: callback to call when a report is generated.
 * - requestClose: callback to close the inspector.
 *
 * Reset-on-element-swap is handled by key-remount in Inspector/index.js (key on
 * the object id): mounting fresh resets the local propagateRename flags with
 * zero stale frame, replacing the old getDerivedStateFromProps. The store-side
 * `ui.discardChanges()` that the class ran in gDSFP now runs in a cleanup effect
 * (see below). Selecting a different object while dirty already discards via
 * ProjectVersion.confirmable before this remounts, so the fresh mount reads a
 * clean `ui.changes`.
 */
const ObjectInspector = observer(function ObjectInspector({
  version,
  object,
  ui,
  onReport,
  onDelete,
  requestClose,
}: ObjectInspectorProps) {
  const [propagateRename, setPropagateRename] = useState(true);
  const [propagateRenameToDependencies, setPropagateRenameToDependencies] = useState(false);

  // The controller is created once per mount (constructor instance field).
  const controllerRef = useRef<EditObjectController | null>(null);
  if (controllerRef.current === null) {
    controllerRef.current = new EditObjectController(version);
  }
  const controller = controllerRef.current;

  // Defensive discard of pending store edits when this inspector unmounts (i.e.
  // when the selected object changes and the key remounts it). Mirrors the
  // ui.discardChanges() the class ran in getDerivedStateFromProps on prop swap.
  // A ref keeps the cleanup pointed at the ui that was current at unmount.
  const uiRef = useRef(ui);
  uiRef.current = ui;
  useEffect(() => {
    return () => {
      uiRef.current.discardChanges();
    };
  }, []);

  const { changes } = ui;

  /**
   * Utitility helpers to:
   * - get the object properties either from the changes or from the object.
   * - check whether a property changed.
   */
  const name = changes.name !== null ? changes.name : object.name;
  const nameChanged = changes.name !== null;
  const description = changes.description !== null ? changes.description : object.description;
  const descriptionChanged = changes.description !== null;
  const constraints = changes.constraints !== null ? changes.constraints : object.constraints;
  const constraintsChanged = changes.constraints !== null;
  const abstract = changes.abstract !== null ? changes.abstract : object.abstract;
  const abstractChanged = changes.abstract !== null;

  /**
   * Get the updates to the attributes in the format that the controller
   * expects.
   */
  // The store views type each property `string | null`, but the form returns
  // real values for the dirty entries, satisfying the command update shapes.
  // Cast at these store→command boundaries.
  const attributeUpdates = (): AttributeUpdates => {
    const { attributesInspector } = ui;
    return {
      removals: attributesInspector.removals,
      edits: attributesInspector.edits,
      additions: attributesInspector.additions,
    } as unknown as AttributeUpdates;
  };

  const mpcUpdates = (): MPCUpdates => {
    const { mpcInspector } = ui;
    return {
      removals: mpcInspector.removals,
      edits: mpcInspector.edits,
      additions: mpcInspector.additions,
    } as unknown as MPCUpdates;
  };

  /**
   * Sort the methods:
   * - OWNED before ACQUIRED
   * - then by name
   */
  const sortedMethods = orderBy(object.methods, ['provenance', 'name'], ['desc', 'asc']);

  /**
   * Discard all changes
   */
  const discardChanges = () => {
    ui.discardChanges();
    setPropagateRename(true);
    setPropagateRenameToDependencies(false);
  };

  /**
   * Apply changes
   */
  const applyChanges = async () => {
    ui.setProjectBusy(true);
    const report = await controller.editObject(
      object.id,
      {
        name,
        description,
        constraints,
        attributes: attributeUpdates(),
        multiplePropagationConstraints: mpcUpdates(),
        abstract,
      },
      {
        eventsAndMethods: propagateRename,
        dependencies: propagateRenameToDependencies,
      }
    );
    if (report.successful) {
      discardChanges();
    }
    ui.setProjectBusy(false);
    if (onReport) onReport(report);
  };

  /**
   * Delete the object
   *
   * The responsibility for executing this has been moved to ProjectVersion to allow to reuse the
   * modal for asking extra information needed before deleting the object (also remove events, what
   * to do with specialised methods in the subtypes, ...)
   */
  const deleteObject = async () => {
    if (onDelete) onDelete(object.id);
  };

  // Render the dependencies in which the object is a dependent
  const renderDependenciesAsDependent = () => {
    if (object.dependenciesAsDependent.length > 0) {
      return object.dependenciesAsDependent.map((dep) => {
        return (
          <div className="dependency mb-[15px] text-[13px] last:mb-0" key={dep.id}>
            <dl className="text-[14px] last:mb-0">
              <dt className="float-left w-[85px] font-medium">Name: </dt>
              <dd className="mb-[5px] ml-[90px] break-words font-mono tracking-[-0.5px] last:mb-0">{dep.master.name}</dd>
              <dt className="float-left w-[85px] font-medium">Dependency: </dt>
              <dd className="mb-[5px] ml-[90px] break-words font-mono tracking-[-0.5px] last:mb-0">{dep.displayName}</dd>
              <dt className="float-left w-[85px] font-medium">Type: </dt>
              <dd className="mb-[5px] ml-[90px] break-words font-mono tracking-[-0.5px] last:mb-0">{dep.type}</dd>
            </dl>
          </div>
        );
      });
    } else {
      return <div>No masters</div>;
    }
  };

  // Render the dependencies in which the object is a master
  const renderDependenciesAsMaster = () => {
    if (object.dependenciesAsMaster.length > 0) {
      return object.dependenciesAsMaster.map((dep) => {
        return (
          <div className="dependency mb-[15px] text-[13px] last:mb-0" key={dep.id}>
            <dl className="text-[14px] last:mb-0">
              <dt className="float-left w-[85px] font-medium">Name:</dt>
              <dd className="mb-[5px] ml-[90px] break-words font-mono tracking-[-0.5px] last:mb-0">{dep.dependent.name}</dd>
              <dt className="float-left w-[85px] font-medium">Dependency:</dt>
              <dd className="mb-[5px] ml-[90px] break-words font-mono tracking-[-0.5px] last:mb-0">{dep.displayName}</dd>
              <dt className="float-left w-[85px] font-medium">Type:</dt>
              <dd className="mb-[5px] ml-[90px] break-words font-mono tracking-[-0.5px] last:mb-0">{dep.type}</dd>
            </dl>
          </div>
        );
      });
    } else {
      return <div>No dependents</div>;
    }
  };

  // Render the methods
  const renderMethods = () => {
    return sortedMethods.map((method) => {
      return (
        <MethodPopover key={method.id} preferPlace="left" method={method}>
          <div className="method" key={method.id}>
            {method.asString()}
          </div>
        </MethodPopover>
      );
    });
  };

  // Render the discard/apply or delete button
  const renderButton = () => {
    if (ui.isEditingMPC) {
      // Sub-editor for MPC's is active, bail out
      return null;
    }
    if (ui.dirty) {
      return (
        <div className="editObject p-2.5">
          <Row gutter={5}>
            <Col md={6}>
              <RSButton
                background={Styles.defaultGradient}
                color={Styles.darkTextColor}
                value="Discard"
                onClick={discardChanges}
              />
            </Col>
            <Col md={6}>
              <RSButton
                background={Styles.blueGradient}
                value="Apply changes"
                onClick={applyChanges}
              />
            </Col>
          </Row>
        </div>
      );
    } else {
      return (
        <div className="removeObject p-2.5">
          <RSButton
            value="Delete object"
            onClick={deleteObject}
            background={Styles.redGradient}
          />
        </div>
      );
    }
  };

  // Render the checkbox 'also rename ...'
  const renderPropagateRename = () => {
    if (nameChanged) {
      return (
        <div className="check_propagate_rename">
          <RSCheckbox
            label="Also rename events and methods"
            checked={propagateRename}
            onChange={asRSChange<RSCheckboxChange>((value: string, checkedState: boolean) =>
              setPropagateRename(checkedState)
            )}
          />
          <RSCheckbox
            label="Also rename dependencies"
            checked={propagateRenameToDependencies}
            onChange={asRSChange<RSCheckboxChange>((value: string, checkedState: boolean) =>
              setPropagateRenameToDependencies(checkedState)
            )}
          />
        </div>
      );
    }
  };

  const renderGeneral = () => {
    const check = controller.checkNameInput(name);
    return (
      <React.Fragment>
        <RSInput
          label="Object name"
          value={name}
          onChange={asRSChange<RSInputChange>((value: string) =>
            changes.storeChange('name', object.name, value)
          )}
          className={nameChanged ? 'dirty' : ''}
          level={rsValue(check.ok ? null : 'danger')}
          text={rsValue(check.ok ? null : check.message)}
        />
        {renderPropagateRename()}
        <RSTextarea
          label="Description"
          value={description}
          onChange={asRSChange<RSTextareaChange>((value: string) =>
            changes.storeChange('description', object.description, value)
          )}
          className={descriptionChanged ? 'dirty' : ''}
        />
        <RSTextarea
          label="Constraints"
          value={constraints}
          onChange={asRSChange<RSTextareaChange>((value: string) =>
            changes.storeChange('constraints', object.constraints, value)
          )}
          className={constraintsChanged ? 'dirty' : ''}
        />
      </React.Fragment>
    );
  };

  const renderDependencies = () => {
    return (
      <React.Fragment>
        <div className="dependency_group mt-[15px] mb-[15px] first:mt-0 last:mb-0">
          <div className="dependency_group_title inspector-subtitle m-0 mb-[5px]">
            Masters <span className="spacer" />
          </div>
          {renderDependenciesAsDependent()}
        </div>
        <div className="dependency_group mt-[15px] mb-[15px] first:mt-0 last:mb-0">
          <div className="dependency_group_title inspector-subtitle m-0 mb-[5px]">
            Dependents <span className="spacer" />
          </div>
          {renderDependenciesAsMaster()}
        </div>
      </React.Fragment>
    );
  };

  /**
   * Render an AttributeInspector for the attributes and render the methods.
   */
  const renderAttributeAndMethods = () => {
    return (
      <React.Fragment>
        <AttributesInspector
          attributes={object.attributes}
          ui={ui.attributesInspector}
          controller={controller}
        />
        <div className="methods">
          <div className="title inspector-subtitle">Methods</div>
          {renderMethods()}
        </div>
      </React.Fragment>
    );
  };

  /**
   * Render a MultiplePropagationConstraintsInspector for the multiple
   * propagation constraints.
   */
  const renderMultiplePropagationConstraints = () => {
    return (
      <MultiplePropagationConstraintsInspector
        ui={ui.mpcInspector}
        object={object}
        constraints={object.multiplePropagationConstraints}
        controller={controller}
      />
    );
  };

  /**
   * Render the inheritance information about the object type.
   */
  const renderInheritance = () => {
    return (
      <React.Fragment>
        <Checkbox
          label="Object is abstract"
          checked={abstract}
          onChange={asRSChange<RSCheckboxChange>((value: string, checkedState: boolean) =>
            changes.storeChange('abstract', object.abstract, checkedState)
          )}
          className={abstractChanged ? 'dirty' : ''}
        />
        <div className="inheritance_group mt-[15px] mb-[15px] first:mt-0 last:mb-0">
          <div className="inheritance_group_title inspector-subtitle m-0 mb-[5px]">
            Supertypes <span className="spacer" />
          </div>
          {object.supertypes.length > 0 ? (
            object.supertypes.map((o) => <div key={o.id}>{o.name}</div>)
          ) : (
            <div>No supertypes</div>
          )}
        </div>
        <div className="inheritance_group mt-[15px] mb-[15px] first:mt-0 last:mb-0">
          <div className="inheritance_group_title inspector-subtitle m-0 mb-[5px]">
            Subtypes <span className="spacer" />
          </div>
          {object.subtypes.length > 0 ? (
            object.subtypes.map((o) => <div key={o.id}>{o.name}</div>)
          ) : (
            <div>No subtypes</div>
          )}
        </div>
      </React.Fragment>
    );
  };

  // Configuration for the InspectorTabs component
  const tabs = () => {
    return [
      {
        key: 'general',
        title: 'General',
        content: renderGeneral(),
      },
      {
        key: 'dependencies',
        title: 'Dependencies',
        content: renderDependencies(),
      },
      {
        key: 'inheritance',
        title: 'Inheritance',
        content: renderInheritance(),
      },
      {
        key: 'attributes-methods',
        title: 'Attributes & methods',
        content: renderAttributeAndMethods(),
      },
      {
        key: 'multiple-propagation-constraints',
        title: 'Multiple propagation constraints',
        content: renderMultiplePropagationConstraints(),
      },
    ];
  };

  return (
    <div className="object-inspector flex h-full flex-col">
      <InspectorTabs tabs={tabs()} />
      {renderButton()}
    </div>
  );
});

export default ObjectInspector;
